/**
 * appAlert — `Alert` que funciona também no navegador.
 *
 * ⚠️ O PROBLEMA: no react-native-web o `Alert` é um stub VAZIO. Literalmente:
 *
 *     class Alert { static alert() {} }
 *     // node_modules/react-native-web/dist/exports/Alert/index.js
 *
 * Então toda `Alert.alert(...)` do app é engolida em silêncio na web — sem
 * diálogo, sem erro, sem aviso no console. Medido em 2026-08-19: clicar em
 * "Sair" no Perfil não produzia absolutamente nada. E o efeito colateral pior
 * não é o botão que não age, e sim a MENSAGEM DE ERRO que nunca aparece: quem
 * digita a senha errada ou falha num upload fica sem retorno algum.
 * São 232 chamadas em 55 arquivos.
 *
 * COMO USAR — troca de import, sem mexer nas chamadas (mesmo padrão do
 * `AppText`, e a "transformação mecânica mínima" que o CLAUDE.md pede):
 *
 *     - import { View, Alert } from 'react-native';
 *     + import { View } from 'react-native';
 *     + import { Alert } from '../utils/appAlert';
 *
 * Em nativo isto reexporta o `Alert` do React Native **sem tocar em nada** —
 * mesma identidade de objeto, mesmo comportamento. O caminho web é o único que
 * ganha implementação.
 *
 * TRÊS OU MAIS BOTÕES (action sheets): `window.confirm` só tem dois caminhos,
 * então esses casos caem em `window.prompt` com as opções numeradas — feio,
 * mas funcional e honesto. Varredura AST de 2026-08-29 achou exatamente 3 usos:
 *   · LiveChat/ChatComposer.js:78     — origem da foto (Câmera / Galeria)
 *   · CommunityScreen.js:883          — motivo da denúncia (4 motivos)
 *   · CommunityScreen.js:919          — menu do post (Denunciar / Bloquear)
 * O caminho de melhoria é um host de modal imperativo montado no App.js, que
 * substituiria confirm/prompt por UI própria — sem mexer nos call-sites, já
 * que todos passam por aqui.
 */

import { Alert as RNAlert, Platform } from 'react-native';

function alertWeb(title, message, buttons) {
    const texto = [title, message].filter(Boolean).join('\n\n');
    const lista = Array.isArray(buttons) ? buttons.filter(Boolean) : [];

    // Sem botões (ou só "OK"): é aviso, não pergunta.
    if (lista.length <= 1) {
        window.alert(texto);
        lista[0]?.onPress?.();
        return;
    }

    const cancelar = lista.find((b) => b.style === 'cancel');
    const opcoes = lista.filter((b) => b !== cancelar);

    // Dois caminhos: confirm cobre.
    if (opcoes.length === 1) {
        if (window.confirm(texto)) {
            opcoes[0].onPress?.();
        } else {
            cancelar?.onPress?.();
        }
        return;
    }

    // Três ou mais: menu numerado via prompt. Entrada vazia, cancelada ou
    // fora da faixa conta como "Cancelar" — nunca dispara uma ação por engano.
    const menu = opcoes.map((b, i) => `${i + 1}) ${b.text}`).join('\n');
    const resposta = window.prompt(`${texto}\n\n${menu}\n\nDigite o número da opção:`, '');
    const idx = Number.parseInt(resposta, 10) - 1;
    if (resposta !== null && Number.isInteger(idx) && idx >= 0 && idx < opcoes.length) {
        opcoes[idx].onPress?.();
    } else {
        cancelar?.onPress?.();
    }
}

export const Alert = Platform.OS === 'web' ? { alert: alertWeb } : RNAlert;

export default Alert;
